import React, { useState, useEffect } from 'react';
import { Calculator, MapPin, Users, Briefcase, Baby, DollarSign, Plus, X } from 'lucide-react';
import { zones, additionalCharges, calculateFare, SHOPPING_STOP_OPTIONS } from '../mock/fareData';

const FareCalculator = () => {
  const [pickupZone, setPickupZone] = useState('');
  const [dropoffZone, setDropoffZone] = useState('');
  const [additionalStops, setAdditionalStops] = useState([]);
  const [passengers, setPassengers] = useState(2);
  const [luggage, setLuggage] = useState(2);
  const [oversizedItems, setOversizedItems] = useState(0);
  const [childrenUnder3, setChildrenUnder3] = useState(0);
  const [shoppingStop, setShoppingStop] = useState('none');
  const [fareBreakdown, setFareBreakdown] = useState(null);

  // 2026-06-26 (LB-3): same-zone guard. When pickup === drop-off the
  // calculator must NOT show any fare (which would otherwise come from
  // fareData.js fallback).
  const isSameZone = Boolean(pickupZone && dropoffZone && pickupZone === dropoffZone);

  // Any time the user puts the form into a same-zone state, clear any
  // previously-computed breakdown so a stale total doesn't linger on screen.
  React.useEffect(() => {
    if (isSameZone && fareBreakdown !== null) {
      setFareBreakdown(null);
    }
  }, [isSameZone, fareBreakdown]);

  const addStop = () => {
    if (additionalStops.length < 3) {
      setAdditionalStops([...additionalStops, '']);
    }
  };

  const removeStop = (index) => {
    setAdditionalStops(additionalStops.filter((_, i) => i !== index));
  };

  const updateStop = (index, value) => {
    const newStops = [...additionalStops];
    newStops[index] = value;
    setAdditionalStops(newStops);
  };

  const handleCalculate = () => {
    if (!pickupZone || !dropoffZone) {
      alert('Please select both pickup and drop-off zones');
      return;
    }

    if (pickupZone === dropoffZone) {
      alert('Pickup and drop-off zones must be different');
      return;
    }

    const breakdown = calculateFare(
      pickupZone,
      dropoffZone,
      passengers,
      luggage,
      oversizedItems,
      childrenUnder3,
      0,
      0,
      additionalStops,
      shoppingStop
    );

    setFareBreakdown(breakdown);
  };

  const availableZones = zones;

  return (
    <div className="min-h-screen bg-gradient-to-b from-cyan-50 to-blue-50 pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-coral-100 px-4 py-2 rounded-full mb-4 border border-coral-200">
            <Calculator className="w-5 h-5 text-coral-600" />
            <span className="text-coral-700 font-semibold">Instant Fare Estimates</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Fare Calculator
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Calculate your fare for any destination across Nassau and Paradise Island
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Calculator Form */}
          <div className="lg:col-span-2">
            <div className="bg-white p-8 rounded-3xl shadow-xl border border-cyan-100">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Trip Details</h2>

              <div className="space-y-6">
                {/* Zone Selection */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-gray-700 font-medium mb-2 flex items-center">
                      <MapPin className="w-5 h-5 text-cyan-600 mr-2" />
                      Pickup Zone
                    </label>
                    <select
                      value={pickupZone}
                      onChange={(e) => setPickupZone(e.target.value)}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all duration-300"
                    >
                      <option value="">Select pickup location...</option>
                      {zones.map((zone) => (
                        <option key={zone.id} value={zone.id}>
                          {zone.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-gray-700 font-medium mb-2 flex items-center">
                      <MapPin className="w-5 h-5 text-coral-600 mr-2" />
                      Drop-off Zone
                    </label>
                    <select
                      value={dropoffZone}
                      onChange={(e) => setDropoffZone(e.target.value)}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all duration-300"
                    >
                      <option value="">Select destination...</option>
                      {zones.filter(z => z.id !== pickupZone).map((zone) => (
                        <option key={zone.id} value={zone.id}>
                          {zone.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Additional Stops */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-gray-700 font-medium flex items-center">
                      <MapPin className="w-5 h-5 text-blue-600 mr-2" />
                      Additional Stops (Optional)
                    </label>
                    {additionalStops.length < 3 && (
                      <button
                        type="button"
                        onClick={addStop}
                        className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition text-sm font-medium"
                      >
                        <Plus className="w-4 h-4" />
                        Add Stop
                      </button>
                    )}
                  </div>
                  
                  {additionalStops.length > 0 && (
                    <div className="space-y-3">
                      {additionalStops.map((stop, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <div className="flex-1">
                            <select
                              value={stop}
                              onChange={(e) => updateStop(index, e.target.value)}
                              className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all duration-300"
                            >
                              <option value="">Select stop {index + 1}...</option>
                              {zones.filter(z => z.id !== pickupZone && z.id !== dropoffZone && !additionalStops.includes(z.id)).map((zone) => (
                                <option key={zone.id} value={zone.id}>
                                  {zone.name}
                                </option>
                              ))}
                            </select>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeStop(index)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                            aria-label="Remove stop"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {additionalStops.length === 0 && (
                    <p className="text-sm text-gray-500 mt-2">
                      Need to make stops along the way? Each additional stop has a <strong>$10 stop fee</strong>; waiting time is billed at <strong>$1.50 per minute</strong> ($90/hour) from the first minute. Add up to 3 additional stops.
                    </p>
                  )}
                  {additionalStops.length > 0 && (
                    <p className="text-sm text-blue-600 mt-2 font-medium">
                      +${(additionalStops.filter(s => s).length * 10).toFixed(2)} stop fee for {additionalStops.filter(s => s).length} additional stop(s). Waiting time: $1.50/min from minute 1.
                    </p>
                  )}
                </div>

                {/* Passenger & Luggage Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-gray-700 font-medium mb-2 flex items-center">
                      <Users className="w-5 h-5 text-cyan-600 mr-2" />
                      Passengers (incl. children 3+)
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="7"
                      value={passengers}
                      onChange={(e) => setPassengers(parseInt(e.target.value))}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all duration-300"
                    />
                    <p className="text-sm text-gray-500 mt-1">First 2 included in base fare</p>
                  </div>

                  <div>
                    <label className="block text-gray-700 font-medium mb-2 flex items-center">
                      <Baby className="w-5 h-5 text-cyan-600 mr-2" />
                      Children Under 3
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="3"
                      value={childrenUnder3}
                      onChange={(e) => setChildrenUnder3(parseInt(e.target.value))}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all duration-300"
                    />
                    <p className="text-sm text-green-600 mt-1 font-medium">Free (not counted)</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-gray-700 font-medium mb-2 flex items-center">
                      <Briefcase className="w-5 h-5 text-cyan-600 mr-2" />
                      Suitcases
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="10"
                      value={luggage}
                      onChange={(e) => setLuggage(parseInt(e.target.value))}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all duration-300"
                    />
                    <p className="text-sm text-gray-500 mt-1">First 2 included, then ${additionalCharges.extraLuggage.toFixed(2)} each</p>
                  </div>

                  <div>
                    <label className="block text-gray-700 font-medium mb-2 flex items-center">
                      <Briefcase className="w-5 h-5 text-coral-600 mr-2" />
                      Oversized Items
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="5"
                      value={oversizedItems}
                      onChange={(e) => setOversizedItems(parseInt(e.target.value))}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all duration-300"
                    />
                    <p className="text-sm text-gray-500 mt-1">Golf bags, boxes, large suitcases — ${additionalCharges.largeBag.toFixed(2)} each</p>
                  </div>
                </div>

                {/* Shopping Plaza Stop — radio (single-select prevents double-charge) */}
                <div data-testid="shopping-plaza-stop-section">
                  <h3 className="text-base font-bold text-gray-900 mb-2">Shopping Plaza Stop</h3>
                  <p className="text-xs text-gray-600 mb-3">
                    Grocery store and liquor store are located in the same shopping plaza. Select one option only.
                  </p>
                  <div className="space-y-2">
                    {Object.values(SHOPPING_STOP_OPTIONS).map((opt) => (
                      <label
                        key={opt.id}
                        data-testid={`shopping-stop-option-${opt.id}`}
                        className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-all duration-200 ${
                          shoppingStop === opt.id
                            ? 'border-cyan-500 bg-cyan-50 ring-2 ring-cyan-500/20'
                            : 'border-gray-300 bg-gray-50 hover:border-cyan-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="shoppingStop"
                          value={opt.id}
                          checked={shoppingStop === opt.id}
                          onChange={(e) => setShoppingStop(e.target.value)}
                          className="mt-1 accent-cyan-600"
                        />
                        <span className="text-sm text-gray-800">{opt.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Same-zone inline warning (LB-3) — sits directly above the
                    Calculate button so the customer sees it immediately. */}
                {isSameZone && (
                  <div
                    data-testid="fare-calculator-same-zone-warning"
                    className="mt-3 px-4 py-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2"
                  >
                    <span className="text-red-600 mt-0.5" aria-hidden="true">⚠</span>
                    <p className="text-sm font-medium text-red-700 leading-relaxed">
                      Please choose a different drop-off zone.
                    </p>
                  </div>
                )}

                {/* Calculate Button — disabled when pickup === drop-off */}
                <button
                  onClick={handleCalculate}
                  data-testid="fare-calculator-calculate-button"
                  disabled={isSameZone}
                  className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-8 py-4 rounded-lg font-bold text-lg hover:from-cyan-600 hover:to-blue-600 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:from-gray-400 disabled:to-gray-500 flex items-center justify-center space-x-2"
                >
                  <Calculator className="w-5 h-5" />
                  <span>Calculate Fare</span>
                </button>
              </div>
            </div>
          </div>

          {/* Fare Breakdown */}
          <div className="lg:col-span-1">
            <div className="bg-gradient-to-br from-cyan-500 to-blue-600 p-8 rounded-3xl shadow-xl text-white sticky top-32">
              <div className="flex items-center space-x-3 mb-6">
                <DollarSign className="w-8 h-8" />
                <h2 className="text-2xl font-bold">Fare Breakdown</h2>
              </div>

              {isSameZone ? (
                <div
                  data-testid="fare-calculator-same-zone-block"
                  className="bg-red-500/15 border border-red-300/40 backdrop-blur-sm p-5 rounded-xl"
                >
                  <p className="text-white font-semibold text-base mb-1">
                    Different drop-off required
                  </p>
                  <p className="text-red-50/90 text-sm leading-relaxed">
                    Please choose a different drop-off zone. We can&rsquo;t calculate a fare when the pickup and drop-off are the same.
                  </p>
                </div>
              ) : fareBreakdown ? (
                <div className="space-y-4">
                  <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl">
                    <p className="text-cyan-100 text-sm mb-1">Base Fare</p>
                    <p className="text-3xl font-bold">${fareBreakdown.baseFare.toFixed(2)}</p>
                  </div>

                  {fareBreakdown.extraPassengers > 0 && (
                    <div className="flex justify-between items-center py-2 border-b border-white/20">
                      <span className="text-cyan-100">Extra Passengers</span>
                      <span className="font-semibold">${fareBreakdown.extraPassengers.toFixed(2)}</span>
                    </div>
                  )}

                  {fareBreakdown.extraLuggage > 0 && (
                    <div className="flex justify-between items-center py-2 border-b border-white/20">
                      <span className="text-cyan-100">Extra Luggage</span>
                      <span className="font-semibold">${fareBreakdown.extraLuggage.toFixed(2)}</span>
                    </div>
                  )}

                  {fareBreakdown.oversizedBags > 0 && (
                    <div className="flex justify-between items-center py-2 border-b border-white/20">
                      <span className="text-cyan-100">Oversized Items</span>
                      <span className="font-semibold">${fareBreakdown.oversizedBags.toFixed(2)}</span>
                    </div>
                  )}

                  {fareBreakdown.additionalStops > 0 && (
                    <div className="flex justify-between items-center py-2 border-b border-white/20">
                      <span className="text-cyan-100">Additional Stops</span>
                      <span className="font-semibold">${fareBreakdown.additionalStops.toFixed(2)}</span>
                    </div>
                  )}

                  {fareBreakdown.bridgeToll > 0 && (
                    <div className="flex justify-between items-center py-2 border-b border-white/20">
                      <span className="text-cyan-100">Bridge Toll</span>
                      <span className="font-semibold">${fareBreakdown.bridgeToll.toFixed(2)}</span>
                    </div>
                  )}

                  {fareBreakdown.shoppingStop > 0 && (
                    <div className="flex justify-between items-center py-2 border-b border-white/20" data-testid="fare-shopping-stop-line">
                      <span className="text-cyan-100">
                        Shopping Plaza Stop
                        <span className="block text-xs text-cyan-200/80">
                          {SHOPPING_STOP_OPTIONS[fareBreakdown.shoppingStopOption]?.display || ''}
                        </span>
                      </span>
                      <span className="font-semibold">${fareBreakdown.shoppingStop.toFixed(2)}</span>
                    </div>
                  )}

                  <div className="bg-white/20 backdrop-blur-sm p-4 rounded-xl mt-6">
                    <p className="text-cyan-100 text-sm mb-2">Total Estimated Fare</p>
                    <p className="text-4xl font-bold" data-testid="fare-total">${fareBreakdown.total.toFixed(2)}</p>
                    <p className="text-cyan-100 text-xs italic mt-3" data-testid="fuel-adjustment-note">
                      <strong className="not-italic">Fare Update Notice:</strong> Due to increased fuel costs in The Bahamas, Taxi-98 fares have been updated. Your estimated fare now reflects this adjustment, while keeping pricing simple, clear, and upfront.
                    </p>
                  </div>

                  <p className="text-cyan-100 text-xs text-center mt-4">
                    Official government rates from LPIA Airport
                  </p>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Calculator className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p className="text-cyan-100">
                    Select your trip details and click Calculate to see the fare
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Rate Information */}
        <div className="mt-12 bg-white p-8 rounded-3xl shadow-xl border border-cyan-100">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Rate Information</h3>
          <div className="mb-6 p-4 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-xl border border-cyan-200">
            <p className="text-gray-700">
              <strong>LPIA Airport Transfers:</strong> Official government-regulated rates apply for all trips to/from LPIA Airport.
            </p>
            <p className="text-gray-700 mt-2">
              <strong>Other Routes:</strong> Fares are based on distance and standard taxi rates across Nassau and Paradise Island.
            </p>
            <p className="text-gray-700 mt-2">
              <strong>Additional Charges:</strong> Extra passengers ($5 each beyond 2), oversized or heavy items ($7 each), additional luggage ($5 each beyond 2 included), car seat ($10 flat), and waiting time ($1.50/min from minute 1, equal to $90/hour) may apply.
            </p>
          </div>

          {/* Fuel & Fare Update — professional long-form notice */}
          <div className="mb-6 p-5 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200" data-testid="fuel-fare-update-notice">
            <h4 className="text-lg font-bold text-gray-900 mb-2">Fuel &amp; Fare Update</h4>
            <p className="text-gray-700 mb-2">
              To continue providing clean, reliable, and professional transportation service, Taxi-98 has updated its fares to reflect the increased cost of fuel in The Bahamas.
            </p>
            <p className="text-gray-700 mb-2">
              Fuel prices are now significantly higher, and this adjustment helps us maintain the level of service our customers expect — including scheduled pickups, airport transfers, private rides, tours, clean vehicle standards, and dependable service.
            </p>
            <p className="text-gray-700 mb-2">
              We have kept our pricing simple and transparent. The updated fare shown at booking is the customer&rsquo;s final estimated fare before any optional add-ons.
            </p>
            <p className="text-gray-700 font-medium">
              Thank you for choosing Taxi-98 Bahamas.
            </p>
          </div>
          
          <h4 className="text-xl font-bold text-gray-900 mb-4">LPIA Airport to Popular Destinations</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {zones.filter(z => !z.isAirport).map((zone) => (
              <div key={zone.id} className="bg-gradient-to-br from-cyan-50 to-blue-50 p-4 rounded-xl border border-cyan-200">
                <p className="text-gray-700 font-medium mb-1">{zone.name}</p>
                <p className="text-2xl font-bold text-cyan-600">${zone.fare.toFixed(2)}</p>
                {zone.bridgeToll && (
                  <p className="text-xs text-gray-600 mt-1">+ ${zone.bridgeToll} bridge toll</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FareCalculator;
Calculator;
